import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  getQuestionsForRelationshipType,
  getDimensionsForRelationshipType,
  AssessmentQuestion 
} from '@/data/relationshipAssessmentQuestions';
import { useRelationshipAssessment, AssessmentResponse } from '@/hooks/useRelationshipAssessment';

interface RelationshipAssessmentFormProps {
  relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other';
  onComplete: () => void;
  onCancel: () => void;
}

const RATING_LABELS = [
  { value: 1, label: 'Not at all' },
  { value: 2, label: 'Rarely' },
  { value: 3, label: 'Sometimes' },
  { value: 4, label: 'Moderately' },
  { value: 5, label: 'Often' },
  { value: 6, label: 'Usually' },
  { value: 7, label: 'Very much' }
];

export function RelationshipAssessmentForm({ 
  relationshipType, 
  onComplete, 
  onCancel 
}: RelationshipAssessmentFormProps) {
  const questions = getQuestionsForRelationshipType(relationshipType);
  const dimensions = getDimensionsForRelationshipType(relationshipType);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  
  const { saveAssessment, isSaving } = useRelationshipAssessment();
  
  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isLastQuestion = currentIndex === questions.length - 1;
  const canProceed = responses[currentQuestion?.id] !== undefined;

  const handleRating = (value: number) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      setShowNotes(true);
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (showNotes) {
      setShowNotes(false);
    } else if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const responseArray: AssessmentResponse[] = Object.entries(responses).map(
      ([questionId, score]) => ({ questionId, score })
    );

    try {
      await saveAssessment(responseArray, relationshipType, notes || undefined);
      onComplete();
    } catch (error) {
      // Error handled in hook
    }
  };

  const currentDimension = dimensions.find(d => d.id === currentQuestion?.dimension);

  return (
    <div className="max-w-2xl mx-auto p-4">
      <Card className="border-none shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              {showNotes ? 'Final thoughts' : `Question ${currentIndex + 1} of ${questions.length}`}
            </span>
            <span className="text-sm font-medium text-primary">
              {Math.round(progress)}% complete
            </span>
          </div>
          <Progress value={showNotes ? 100 : progress} className="h-2" />
        </CardHeader>

        <CardContent>
          <AnimatePresence mode="wait">
            {showNotes ? (
              <motion.div
                key="notes"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <div>
                  <CardTitle className="text-xl mb-2">
                    Any additional reflections?
                  </CardTitle>
                  <CardDescription>
                    Optional: Add any thoughts about your relationship that the questions didn't capture.
                  </CardDescription>
                </div>

                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What's on your mind about this relationship?"
                  rows={4}
                  className="resize-none"
                />

                <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                  <p>
                    Remember: This is your personal snapshot, not a test. There are no "right" answers - 
                    it's about noticing where you are and opening conversation.
                  </p>
                </div>
              </motion.div>
            ) : currentQuestion && (
              <motion.div
                key={currentQuestion.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                {currentDimension && (
                  <div 
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: `${currentDimension.color}20`,
                      color: currentDimension.color 
                    }}
                  >
                    {currentDimension.name}
                  </div>
                )}

                <div>
                  <CardTitle className="text-xl mb-2">
                    {currentQuestion.question}
                  </CardTitle>
                  <CardDescription>
                    {currentQuestion.description}
                  </CardDescription>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm text-muted-foreground">
                    How true is this for your relationship?
                  </Label>
                  
                  <div className="grid grid-cols-7 gap-1">
                    {RATING_LABELS.map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => handleRating(value)}
                        className={`
                          flex flex-col items-center p-2 rounded-lg transition-all
                          ${responses[currentQuestion.id] === value
                            ? 'bg-primary text-primary-foreground scale-105'
                            : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground'
                          }
                        `}
                      >
                        <span className="text-lg font-semibold">{value}</span>
                        <span className="text-[10px] leading-tight text-center hidden sm:block">
                          {label}
                        </span>
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex justify-between text-xs text-muted-foreground px-1">
                    <span>Not at all</span>
                    <span>Very much</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-between mt-8 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={currentIndex === 0 && !showNotes ? onCancel : handlePrevious}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {currentIndex === 0 && !showNotes ? 'Cancel' : 'Back'}
            </Button>

            {showNotes ? (
              <Button 
                onClick={handleSubmit} 
                disabled={isSaving}
                className="min-w-[120px]"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Save Snapshot
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed}
              >
                {isLastQuestion ? 'Review' : 'Next'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
